import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'ko' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'preferredLanguage';

// 로그인 여부와 무관하게(공개 페이지 포함) 앱 전체에서 공유하는 언어 상태.
// AuthContext에 의존하지 않는 독립 컨텍스트로 둬서, 비로그인 상태로도 노출되는
// 컴포넌트(TermsModal 등)에서 AuthProvider 없이도 그대로 쓸 수 있게 한다.
// 로그인 사용자의 user.preferredLanguage 동기화는 이 컨텍스트를 쓰는 쪽
// (DashboardLayout)에서 별도로 처리한다.
export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'en' || stored === 'ko' ? stored : 'ko';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return ctx;
};
